import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  formatMoneda,
  restanteDistribucion,
  sumarImportesCuentas,
} from "./utils";

const nuevaFila = (importeSugerido = "") => ({
  key: `cuenta-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  codigoBanco: "",
  id_bancos_cuentas: "",
  importe: importeSugerido,
});

const ImporteCuenta = ({ value, label, onCommit }) => {
  const [borrador, setBorrador] = useState(
    value != null && value !== "" ? String(value) : ""
  );
  const enfocadoRef = useRef(false);

  useEffect(() => {
    if (enfocadoRef.current) return;
    setBorrador(value != null && value !== "" ? String(value) : "");
  }, [value]);

  const confirmar = () => {
    enfocadoRef.current = false;
    const raw = String(borrador ?? "").trim().replace(",", ".");
    if (raw === "") {
      setBorrador("");
      if (String(value ?? "") !== "") onCommit("");
      return;
    }
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) {
      setBorrador(value != null && value !== "" ? String(value) : "");
      return;
    }
    const redondeado = Math.round(n * 100) / 100;
    const next = String(redondeado);
    setBorrador(next);
    if (String(value ?? "") !== next) onCommit(next);
  };

  return (
    <TextField
      fullWidth
      size="small"
      type="number"
      label={label}
      value={borrador}
      onFocus={() => {
        enfocadoRef.current = true;
      }}
      onChange={(e) => setBorrador(e.target.value)}
      onBlur={confirmar}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.target.blur();
        }
      }}
      onWheel={(e) => e.target.blur()}
      InputProps={{
        startAdornment: <InputAdornment position="start">$</InputAdornment>,
      }}
      inputProps={{ min: 0.01, step: 0.01 }}
    />
  );
};

const SeleccionCuentasOrigen = ({
  cuentasOrigen = [],
  onChange,
  cuentasBancarias = [],
  loadingCuentas,
  totalAPagar = 0,
}) => {
  const bancosUnicos = useMemo(() => {
    const map = new Map();
    cuentasBancarias.forEach((c) => {
      const key = c.codigo ?? c.razon_social;
      if (!map.has(key)) {
        map.set(key, { codigo: key, nombre: c.razon_social || c.codigo });
      }
    });
    return Array.from(map.values());
  }, [cuentasBancarias]);

  const idsUsados = useMemo(
    () =>
      new Set(
        cuentasOrigen
          .filter((r) => r.id_bancos_cuentas)
          .map((r) => String(r.id_bancos_cuentas))
      ),
    [cuentasOrigen]
  );

  const asignado = sumarImportesCuentas(cuentasOrigen);
  const restante = restanteDistribucion(cuentasOrigen, totalAPagar);
  const distribucionOk = Math.abs(restante) < 0.02 && asignado > 0;

  const actualizarFila = (key, cambios) => {
    onChange(
      cuentasOrigen.map((fila) =>
        fila.key === key ? { ...fila, ...cambios } : fila
      )
    );
  };

  const eliminarFila = (key) => {
    onChange(cuentasOrigen.filter((fila) => fila.key !== key));
  };

  const agregarFila = () => {
    const sugerido =
      restante > 0 ? String(Math.round(restante * 100) / 100) : "";
    onChange([...cuentasOrigen, nuevaFila(sugerido)]);
  };

  const cuentasPorBanco = (codigoBanco) => {
    if (!codigoBanco) return [];
    return cuentasBancarias.filter(
      (c) => (c.codigo ?? c.razon_social) === codigoBanco
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">
            Total a pagar:{" "}
            <strong>{formatMoneda(totalAPagar)}</strong>
            {" · "}
            Asignado: <strong>{formatMoneda(asignado)}</strong>
            {" · "}
            {restante > 0.02
              ? "Falta asignar: "
              : restante < -0.02
                ? "Sobra: "
                : "Restante: "}
            <strong style={{ color: Math.abs(restante) >= 0.02 ? "#d32f2f" : undefined }}>
              {formatMoneda(Math.abs(restante) < 0.02 ? 0 : Math.abs(restante))}
            </strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Podés dividir el importe entre varias cuentas. El total se actualiza
            al salir del importe.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={agregarFila}
          disabled={loadingCuentas}
        >
          Agregar banco / cuenta
        </Button>
      </Box>

      {loadingCuentas && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="caption">Cargando cuentas…</Typography>
        </Box>
      )}

      {cuentasOrigen.length === 0 && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Agregá al menos una cuenta origen y distribuí el importe entre ellas.
        </Alert>
      )}

      {cuentasOrigen.map((fila, index) => {
        const cuentasFiltradas = cuentasPorBanco(fila.codigoBanco);

        return (
          <Box
            key={fila.key}
            sx={{ mb: 1.5, p: 1.5, border: "1px solid #e8e8e8", borderRadius: 1 }}
          >
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small" disabled={loadingCuentas}>
                  <InputLabel>Banco origen</InputLabel>
                  <Select
                    label="Banco origen"
                    value={fila.codigoBanco ?? ""}
                    onChange={(e) =>
                      actualizarFila(fila.key, {
                        codigoBanco: e.target.value,
                        id_bancos_cuentas: "",
                      })
                    }
                  >
                    {bancosUnicos.map((b) => (
                      <MenuItem key={b.codigo} value={b.codigo}>
                        {b.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl
                  fullWidth
                  size="small"
                  disabled={loadingCuentas || !fila.codigoBanco}
                >
                  <InputLabel>Cuenta origen</InputLabel>
                  <Select
                    label="Cuenta origen"
                    value={fila.id_bancos_cuentas ?? ""}
                    onChange={(e) =>
                      actualizarFila(fila.key, {
                        id_bancos_cuentas: e.target.value,
                      })
                    }
                  >
                    {cuentasFiltradas.map((c) => {
                      const id = String(c.id_bancos_cuentas);
                      const ocupada =
                        idsUsados.has(id) &&
                        String(fila.id_bancos_cuentas) !== id;
                      return (
                        <MenuItem key={id} value={c.id_bancos_cuentas} disabled={ocupada}>
                          {c.nro_cuenta
                            ? `${c.nro_cuenta}${c.cbu ? ` — ${c.cbu}` : ""}`
                            : c.razon_social}
                          {ocupada ? " (ya seleccionada)" : ""}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={10} sm={4}>
                <ImporteCuenta
                  label={`Importe ${index + 1}`}
                  value={fila.importe}
                  onCommit={(importe) =>
                    actualizarFila(fila.key, { importe })
                  }
                />
              </Grid>
              <Grid item xs={2} sm={1} sx={{ textAlign: "center" }}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => eliminarFila(fila.key)}
                  aria-label="Quitar cuenta"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        );
      })}

      {cuentasOrigen.length > 0 && !distribucionOk && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          La suma de los importes debe coincidir con el total a pagar (
          {formatMoneda(totalAPagar)}).
        </Alert>
      )}
    </Box>
  );
};

export { nuevaFila };
export default SeleccionCuentasOrigen;
